import React from "react";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { formatProductTypeDomainName } from "@/lib/formatProductType";
import { formatCanonicalCategoryLeaf } from "@/lib/formatCanonicalCategory";
import { MappingStatusBadge } from "../MappingStatusBadge";
import { ConfidenceBadge } from "../ConfidenceBadge";
import { marketplaceLogo } from "@/lib/marketplaceLogo";
import { rowBase, selectedRow } from "./MappingTableRow.styles";
import type { MappingTableRowProps } from "./MappingTableRow.types";

function formatMappedDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const MappingTableRow: React.FC<MappingTableRowProps> = ({
  mapping,
  isSelected = false,
  onSelect,
  onDelete,
}) => {
  const logo = marketplaceLogo(mapping.marketplace);
  const productTypeDomainName =
    formatProductTypeDomainName(mapping.productType) ||
    formatProductTypeDomainName(mapping.displayName) ||
    mapping.displayName;
  const canonicalCategoryLeaf = formatCanonicalCategoryLeaf(
    mapping.canonicalCategory,
  );

  return (
    <tr
      data-testid={`mapping-row-${mapping.id}`}
      className={cn(rowBase, isSelected && selectedRow)}
      onClick={() => onSelect(mapping)}
      aria-selected={isSelected}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {logo ? (
            <img
              src={logo}
              alt={mapping.marketplace}
              className="h-10 w-10 object-contain"
            />
          ) : (
            <span className="h-10 w-10 rounded bg-slate-200 dark:bg-slate-700" />
          )}
          <p
            className="text-sm font-medium text-slate-900 dark:text-slate-100"
            title={mapping.productType || mapping.displayName}
          >
            {productTypeDomainName}
          </p>
        </div>
      </td>
      <td className="px-4 py-3">
        {canonicalCategoryLeaf ? (
          <span
            className="text-sm font-medium text-indigo-600 dark:text-indigo-400"
            title={mapping.canonicalCategory ?? canonicalCategoryLeaf}
          >
            {canonicalCategoryLeaf}
          </span>
        ) : (
          <span className="text-sm italic text-slate-400 dark:text-slate-500">
            Not mapped
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <MappingStatusBadge status={mapping.status} />
      </td>
      <td className="px-4 py-3">
        <ConfidenceBadge confidence={mapping.confidence} />
      </td>
      <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
        {formatMappedDate(mapping.mappedAt)}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
        {mapping.mappedBy ?? "—"}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(mapping);
            }}
            title="View details"
            aria-label="View mapping session details"
          >
            <Eye className="w-3.5 h-3.5" />
          </Button>
          {mapping.status === "mapped" && (
            <Button asChild variant="ghost" size="sm" className="h-7 w-7 p-0">
              <Link
                to={`/catalog/mapping?sessionId=${encodeURIComponent(mapping.mappingSessionId ?? mapping.id)}&edit=1`}
                onClick={(event) => event.stopPropagation()}
                title="Edit one row"
                aria-label="Edit one row of this finished session"
                data-testid={`mapping-edit-${mapping.id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </Link>
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 dark:text-rose-400"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(mapping);
            }}
            title="Delete"
            aria-label="Delete this mapping session"
            data-testid={`mapping-delete-${mapping.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
};
